import { InputBase, makeStyles, Theme } from "@material-ui/core";
import React, { useRef } from "react";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  input: {
    width: 48,
    height: 56,
    fontSize: 32,
    textAlign: "center" as const,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
    transition: "border-color 0.2s",
    '&:focus': {
      borderColor: theme.palette.primary.main,
      outline: 'none',
    },
  },
}));

interface PinCodeInputProps
{
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  IsEnabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
}

const PinCodeInput: React.FC<PinCodeInputProps> = ({ length = 6, value, onChange, disabled, IsEnabled = true, inputRef }) =>
{
  const classes = useStyles();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) =>
  {
    const val = e.target.value.replace(/\D/g, "");
    if(!val) return;
    let newValue = value.split("");
    newValue[idx] = val[val.length - 1];
    for(let i = idx + 1; i < length; i++)
    {
      if(!newValue[i]) newValue[i] = "";
    }
    onChange(newValue.join("").slice(0, length));
    if(val && idx < length - 1)
    {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) =>
  {
    if(e.key === "Backspace" && !value[idx] && idx > 0)
    {
      onChange(value.slice(0, idx - 1) + value.slice(idx));
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if(e.key === "ArrowLeft" && idx > 0)
    {
      inputsRef.current[idx - 1]?.focus();
      e.preventDefault();
    }
    if(e.key === "ArrowRight" && idx < length - 1)
    {
      inputsRef.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) =>
  {
    const pasted = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, length);
    if(pasted)
    {
      onChange(pasted);
      setTimeout(() =>
      {
        if(inputsRef.current[pasted.length - 1])
        {
          inputsRef.current[pasted.length - 1]?.focus();
        }
      }, 0);
    }
    e.preventDefault();
  };

  return (
    <div className={classes.root}>
      {Array.from({ length }).map((_, idx) => (
        <InputBase
          key={idx}
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center" },
            "aria-label": `Digit ${idx + 1}`,
            inputMode: "numeric",
            pattern: "[0-9]*",
          }}
          className={classes.input}
          value={value[idx] || ""}
          onChange={e => handleChange(e as React.ChangeEvent<HTMLInputElement>, idx)}
          onKeyDown={e => handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, idx)}
          onPaste={handlePaste}
          inputRef={el =>
          {
            inputsRef.current[idx] = el;
            if(idx === 0 && inputRef)
            {
              // Assign the first input to the forwarded ref
              (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }
          }}
          disabled={disabled || !IsEnabled}
        />
      ))}
    </div>
  );
};

export default PinCodeInput;
